<template>
    <div class="page-dunning-config-edit">
        <DRow>
            <DCol>
                <DTabs class="mb-3">
                    <DTabItem
                        id="dunning-general"
                        :route-url="$route('settings.dunning-settings.create')"
                        :active="['settings.dunning-settings.create'].includes($route().current())"
                        :label="__('settings.dunning_settings.tabs.general')"
                    />
                    <DTabItem
                        id="dunning-fees-and-limits"
                        :route-url="$route('settings.dunning-settings.fees-and-limits.index')"
                        :active="['settings.dunning-settings.fees-and-limits.index', 'settings.dunning-settings.fees-and-limits.edit'].includes($route().current())"
                        :label="__('settings.dunning_settings.tabs.fees_and_limits')"
                    />
                </DTabs>
            </DCol>
        </DRow>

        <DCard
            :title="`${__('settings.dunning_fees_and_limits.edit.title')} - ${marketRegion.name}`"
            padding
        >
            <DFormRow
                :label="__('settings.dunning_fees_and_limits.table.headers.currency_code')"
                :hint="__('settings.dunning_fees_and_limits.table.headers.currency_code_hint')"
            >
                <span>{{ marketRegion.currency_code }}</span>
            </DFormRow>

            <DFormRow
                :label="__('settings.dunning_fees_and_limits.form.dunning_fees')"
                :is-required="true"
                :hint="__('settings.dunning_fees_and_limits.form.dunning_fees_hint')"
            >
                <DSwitch
                    v-model="form.dunning_fees"
                    color="success"
                    variant="3d"
                />
            </DFormRow>
        </DCard>

        <template v-if="form.dunning_fees">
            <DCard
                v-for="reminder in reminderCards"
                :key="reminder.key"
                :title="__(`settings.dunning_fees_and_limits.card.${reminder.label}`)"
                padding
            >
                <DFormRow
                    :label="__('settings.dunning_fees_and_limits.table.private')"
                    :is-required="true"
                    :is-vertical="true"
                    :hint="__(`settings.dunning_fees_and_limits.card.${reminder.label}_private_hint`)"
                >
                    <DInput
                        :id="reminder.private"
                        v-model="form[reminder.private]"
                        add-label-classes="required"
                        :is-valid="!form.errors[reminder.private]"
                        type="number"
                        step="0.001"
                        min="0"
                        max="999999999.999"
                    />
                    <DAlert
                        v-if="form.errors[reminder.private]"
                        color="danger"
                        class="mt-2"
                    >
                        {{ form.errors[reminder.private] }}
                    </DAlert>
                </DFormRow>
                <DFormRow
                    :label="__('settings.dunning_fees_and_limits.table.business')"
                    :is-required="true"
                    :is-vertical="true"
                    :hint="__(`settings.dunning_fees_and_limits.card.${reminder.label}_business_hint`)"
                >
                    <DInput
                        :id="reminder.business"
                        v-model="form[reminder.business]"
                        add-label-classes="required"
                        :is-valid="!form.errors[reminder.business]"
                        type="number"
                        step="0.001"
                        min="0"
                        max="999999999.999"
                    />
                    <DAlert
                        v-if="form.errors[reminder.business]"
                        color="danger"
                        class="mt-2"
                    >
                        {{ form.errors[reminder.business] }}
                    </DAlert>
                </DFormRow>
            </DCard>
        </template>

        <DCard
            :title="__('settings.dunning_settings.form.more_settings')"
            padding
        >
            <DFormRow
                :label="__('settings.dunning_fees_and_limits.form.mrr_threshold_for_dunning_tasks')"
                :is-required="true"
                :is-vertical="true"
                :hint="__('settings.dunning_fees_and_limits.form.mrr_threshold_for_dunning_tasks_hint')"
            >
                <DInput
                    id="mrr_dunning_threshold"
                    v-model="form.mrr_dunning_threshold"
                    add-label-classes="required"
                    :is-valid="!form.errors.mrr_dunning_threshold"
                    type="number"
                    min="1"
                    max="1000000"
                />
                <DAlert
                    v-if="form.errors.mrr_dunning_threshold"
                    color="danger"
                    class="mt-2"
                >
                    {{ form.errors.mrr_dunning_threshold }}
                </DAlert>
            </DFormRow>

            <DFormRow
                :label="__('settings.dunning_fees_and_limits.form.damage_compensation_fee')"
                :is-required="true"
                :is-vertical="true"
                :hint="__('settings.dunning_fees_and_limits.form.damage_compensation_fee_hint')"
            >
                <DInput
                    id="damage_compensation_fee"
                    v-model="form.damage_compensation_fee"
                    add-label-classes="required"
                    :is-valid="!form.errors.damage_compensation_fee"
                    type="number"
                    step="0.001"
                    min="0"
                    max="999999999.999"
                />
                <DAlert
                    v-if="form.errors.damage_compensation_fee"
                    color="danger"
                    class="mt-2"
                >
                    {{ form.errors.damage_compensation_fee }}
                </DAlert>
            </DFormRow>
        </DCard>

        <DFormControls
            :cancel-route="$route('settings.dunning-settings.fees-and-limits.index')"
            :dirty="form.isDirty"
            @submit="submit"
        />
    </div>
</template>

<script setup>
import { useForm } from '@inertiajs/vue3'
import { route } from '~@/Composables/route'
import { __ } from '~@/Composables/translation'

const props = defineProps({
    marketRegion: {
        type: Object,
        default: () => { return {} }
    },
    dunningFeesAndLimits: {
        type: Object,
        default: () => { return {} }
    }
})

const reminderCards = [
    { key: 'first', label: 'first_reminder', private: 'first_dunning_fee_private', business: 'first_dunning_fee_business' },
    { key: 'second', label: 'second_reminder', private: 'second_dunning_fee_private', business: 'second_dunning_fee_business' },
    { key: 'third', label: 'last_reminder', private: 'third_dunning_fee_private', business: 'third_dunning_fee_business' }
]

/**
 * The decimal(12,3) columns arrive as strings from the backend ("4.500"), which a number input
 * would render verbatim. Cast them so the fields show 4.5 instead.
 */
const amountFields = [
    ...reminderCards.flatMap(reminder => [reminder.private, reminder.business]),
    'mrr_dunning_threshold',
    'damage_compensation_fee'
]

function toNumber (value) {
    if (value === null || value === undefined || value === '') {
        return null
    }

    const number = Number(value)

    return Number.isNaN(number) ? null : number
}

const form = useForm({
    dunning_fees: props.dunningFeesAndLimits.dunning_fees,
    ...Object.fromEntries(amountFields.map(field => [field, toNumber(props.dunningFeesAndLimits[field])]))
})

function submit () {
    // DInput always emits the raw input value, so every amount is a string again once edited.
    form
        .transform(data => ({
            ...data,
            ...Object.fromEntries(amountFields.map(field => [field, toNumber(data[field])]))
        }))
        .put(route('settings.dunning-settings.fees-and-limits.update', { marketRegion: props.marketRegion.id }))
}
</script>

<style lang="scss" scoped>
.page-dunning-config-edit {
    display: grid;
    grid-gap: 25px;
}
</style>
